import {
  CommodityTypeList,
  DrawerCloseStyled,
  DrawerFooterButtonStyled,
  DrawerHeaderStyled,
  DrawerSubHeaderStyled,
  Milestones as MilestoneList,
} from '@bechtel/client-common';
import { WeldMilestoneType } from '@bechtel/client-common/src/types';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  FormControl,
  Icon,
  IconSize,
  Radio,
  Select,
  SideSheetBody,
  SideSheetContentCol,
  SideSheetFooter,
  Text,
  TextFieldAppearance,
  TextFieldSize,
} from '@bechtel/gds';
import { SortOrder } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Field, Form, Formik } from 'formik';
import { find, isEqual } from 'lodash';
import { FC } from 'react';
import { initialDrawerValues, searchByList, sortByList } from '../worklist-drawer/drawer-state';
import { WorkListDrawerValueProps } from '../worklist/types';
import { PlanStatusList } from './plan-status-list';
import { DrawerBackdropStyled, DrawerOuterStyled, ListSelectionStyled } from './styled';
interface WorkListDrawerProps {
  close: () => void;
  initialValues: WorkListDrawerValueProps;
  onSubmit: (values: WorkListDrawerValueProps) => void;
}
const SortSelectedItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" family="graphik">
      {i18n._(item.label)}
    </Text>
  );
};

const SortListItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000">
      {i18n._(item.label)}
    </Text>
  );
};
export const WorkListDrawer: FC<WorkListDrawerProps> = ({ close, initialValues, onSubmit }) => {
  const { i18n } = useLingui();

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        close();
        onSubmit(values);
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ setFieldValue, values, isValid, dirty, setValues }) => {
        const isAsc = values.sortDir === SortOrder.ASC;
        return (
          <Form role="form">
            <DrawerBackdropStyled>
              <DrawerOuterStyled>
                <DrawerHeaderStyled title={t`Filters`}>
                  <DrawerCloseStyled onClick={close}></DrawerCloseStyled>
                </DrawerHeaderStyled>
                <SideSheetBody>
                  <SideSheetContentCol>
                    <Box px={{ sm: '400' }} py={{ sm: '500' }}>
                      <Box mr={{ sm: '400' }} flexGrow={{ sm: 1 }}>
                        <FormControl style={{ flexGrow: 1 }} label={t`Sort By`}>
                          <Select
                            size={TextFieldSize.MEDIUM}
                            appearance={TextFieldAppearance.NORMAL}
                            items={sortByList}
                            itemComponent={SortListItem}
                            selectedComponent={SortSelectedItem}
                            initialSelectedItem={find(sortByList, { value: values.sortBy })}
                            onSelectedItemChange={(changes: any) => {
                              setFieldValue('sortBy', changes.selectedItem.value);
                            }}
                          />
                        </FormControl>
                      </Box>
                      <Box>
                        <Button
                          appearance={ButtonAppearance.SECONDARY}
                          size={ButtonSize.MEDIUM}
                          onClick={() => {
                            const newValue = isAsc ? SortOrder.DESC : SortOrder.ASC;
                            setFieldValue('sortDir', newValue);
                          }}
                          data-testid="sort-button"
                        >
                          <Icon
                            fillColor="blue.800"
                            icon={isAsc ? 'sort-amount-down' : 'sort-amount-up'}
                            size={IconSize.MEDIUM}
                          />
                        </Button>
                      </Box>
                    </Box>
                    <DrawerSubHeaderStyled>
                      <Trans>Search By</Trans>
                    </DrawerSubHeaderStyled>
                    {searchByList.map((scope) => {
                      return (
                        <ListSelectionStyled
                          key={scope.id}
                          onClick={() => {
                            setFieldValue('searchBy', scope.searchByTerm.type);
                          }}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Radio checked={values.searchBy === scope.searchByTerm.type} readOnly />
                          </Box>
                          <Text color="grey.1000" size="400">
                            {i18n._(scope.label)}
                          </Text>
                        </ListSelectionStyled>
                      );
                    })}
                    <DrawerSubHeaderStyled>
                      <Trans>Filter By</Trans>
                    </DrawerSubHeaderStyled>
                    <Field name="commodityTypeId" component={CommodityTypeList} excludeCommodityTypes={['Weld']} />
                    <Field name="milestoneId" component={MilestoneList} excludeMilestoneTypes={[WeldMilestoneType.FITUP, WeldMilestoneType.INSTALL]} />
                    <Field name="planStatusType" component={PlanStatusList} />
                    <Box p={{ sm: '500' }}>
                      <Button
                        data-testid="reset-button"
                        size={ButtonSize.MEDIUM}
                        css={`
                          color: ${({ theme }) => theme.colors.blue[800]};
                          font-weight: ${({ theme }) => theme.fontWeights[600]};
                        `}
                        appearance={ButtonAppearance.SECONDARY}
                        disabled={isEqual(initialDrawerValues, values)}
                        onClick={() => {
                          setValues(initialDrawerValues);
                        }}
                      >
                        <Trans>Reset All Filters</Trans>
                      </Button>
                    </Box>
                  </SideSheetContentCol>
                </SideSheetBody>
                <SideSheetFooter>
                  <Box justifyContent={{ sm: 'flex-end' }}>
                    <Box mr={{ sm: '400' }}>
                      <DrawerFooterButtonStyled
                        data-testid="cancel-button"
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={close}
                      >
                        <Trans>Cancel</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                    <Box>
                      <DrawerFooterButtonStyled
                        data-testid="apply-filter-button"
                        type="submit"
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        disabled={!dirty || !isValid}
                      >
                        <Trans>Apply Filters</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                  </Box>
                </SideSheetFooter>
              </DrawerOuterStyled>
            </DrawerBackdropStyled>
          </Form>
        );
      }}
    </Formik>
  );
};
